// iter127 — Single-risk impact graph (`/explore/risks/:id/impact`).
//
// Shows the risk + its linked signals, supplier, governing controls and
// responsible owner as a node-link diagram. Plus a "Who is impacted"
// panel listing peer risks owned by the same person.
//
// Pure-CSS SVG layout (no external graph library) — keeps the page light
// and the visual aligned with the Big-4 board pack aesthetic.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Radio, Building2, ShieldCheck, User, Printer } from "lucide-react";
import { api } from "@/lib/api";

const NODE_META = {
  risk:     { Icon: AlertTriangle, color: "#7737F6", bg: "#F4E9FF" },
  signal:   { Icon: Radio,         color: "#B0341E", bg: "#FBE0DC" },
  supplier: { Icon: Building2,     color: "#8B5A0C", bg: "#FFE4D0" },
  control:  { Icon: ShieldCheck,   color: "#1c5a2c", bg: "#DDEBE0" },
  owner:    { Icon: User,          color: "#4a5b75", bg: "#ECECEC" },
};

const EDGE_LABEL = {
  signal_triggers_risk: "triggers",
  supplier_drives_risk: "drives",
  control_mitigates_risk: "mitigates",
  owner_accountable_for_risk: "accountable for",
};

export default function RiskImpact() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/explore/risks/${id}/impact`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Unable to load"));
  }, [id]);

  if (error) return <Centred>{error}</Centred>;
  if (!data) return <Centred>Loading impact graph…</Centred>;

  // Group nodes by type for layout.
  const groups = {
    signal: data.nodes.filter((n) => n.type === "signal"),
    supplier: data.nodes.filter((n) => n.type === "supplier"),
    control: data.nodes.filter((n) => n.type === "control"),
    owner: data.nodes.filter((n) => n.type === "owner"),
  };
  const riskNode = data.nodes.find((n) => n.type === "risk");

  return (
    <div className="min-h-screen bg-white" data-testid="explore-risk-impact">
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Link to="/explore" className="inline-flex items-center gap-1 text-xs text-ek-ink/60 hover:text-ek-ink">
            <ArrowLeft size={14} /> Back to Explore
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="risk-impact-print-btn"
            className="ek-btn-outline flex items-center gap-1.5 !py-2 text-sm"
            title="Print or save the impact view as a PDF"
          >
            <Printer size={14} /> Print
          </button>
        </div>
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[2px] text-ek-ink/50">Risk impact</p>
          <h1 className="ek-heading mt-2 text-3xl sm:text-4xl">{data.risk.title}</h1>
          <p className="mt-3 text-sm text-ek-ink/65">
            <span className="font-mono">{data.risk.risk_id}</span> ·
            theme <span className="font-semibold">{data.risk.theme_id}</span> ·
            residual <span className="font-semibold">{data.risk.residual_score}/25</span> ·
            status <span className="font-semibold">{data.risk.status}</span>
          </p>
        </header>

        <div className="grid grid-cols-12 gap-8">
          <section className="col-span-12 lg:col-span-8" data-testid="impact-graph">
            <ImpactBlueprint riskNode={riskNode} groups={groups} edges={data.edges} />
          </section>

          <aside className="col-span-12 lg:col-span-4 space-y-6">
            <Panel title="Who else is impacted">
              {(data.impacted_peers || []).length === 0 && (
                <p className="text-sm text-ek-ink/55">This owner has no other risks within your visibility.</p>
              )}
              <ul className="divide-y divide-ek-ink/10">
                {(data.impacted_peers || []).map((p) => (
                  <li key={p.risk_id} className="py-3">
                    <Link to={`/explore/risks/${p.risk_id}/impact`} className="block hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`peer-risk-${p.risk_id}`}>
                      <p className="text-sm font-semibold text-ek-ink truncate">{p.title}</p>
                      <p className="text-xs text-ek-ink/55 mt-1">{p.risk_id} · residual {p.residual_score}/25</p>
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel title="Node summary">
              <ul className="space-y-1.5 text-sm text-ek-ink/80">
                {Object.entries(data.node_count_by_type).map(([k, n]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="capitalize">{k}</span>
                    <span className="font-mono text-ek-ink/60">{n}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          </aside>
        </div>
      </div>
    </div>
  );
}

function ImpactBlueprint({ riskNode, groups, edges }) {
  // Simple radial-ish layout — risk in the centre, neighbours stacked
  // in 4 columns by type. Edges are labelled below each neighbour list.
  if (!riskNode) return null;
  const cols = [
    { key: "signal",   title: "Signals (drivers)",   list: groups.signal },
    { key: "supplier", title: "Linked supplier",     list: groups.supplier },
    { key: "control",  title: "Governing controls",  list: groups.control },
    { key: "owner",    title: "Accountable owner",   list: groups.owner },
  ];
  return (
    <div className="space-y-6">
      {/* The risk itself */}
      <div className="rounded-lg border border-ek-purple/30 bg-ek-purple-fog px-5 py-4">
        <p className="text-[10px] font-semibold uppercase tracking-[1.4px] text-ek-purple">This risk</p>
        <p className="text-lg font-semibold text-ek-ink mt-1">{riskNode.label}</p>
        <p className="text-xs text-ek-ink/60 mt-1">
          residual {riskNode.meta.residual_score}/25 · inherent {riskNode.meta.inherent_score}/25
        </p>
      </div>
      {/* Four neighbour columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cols.map((c) => (
          <div key={c.key} className="rounded border border-ek-ink/10 p-4" data-testid={`impact-col-${c.key}`}>
            <p className="text-[10px] font-semibold uppercase tracking-[1.4px] text-ek-ink/55">{c.title}</p>
            {c.list.length === 0 && <p className="text-sm text-ek-ink/50 mt-3">None recorded.</p>}
            <ul className="mt-3 space-y-2">
              {c.list.map((n) => (
                <NodeRow key={n.id} node={n} edge={edges.find((e) => e.from === n.id)} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function NodeRow({ node, edge }) {
  const meta = NODE_META[node.type] || NODE_META.owner;
  const Icon = meta.Icon;
  return (
    <li className="flex items-start gap-3">
      <div className="flex-shrink-0 rounded p-1.5" style={{ background: meta.bg, color: meta.color }}>
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-ek-ink truncate">{node.label}</p>
        {edge && <p className="text-[10px] uppercase tracking-wider text-ek-ink/45 mt-0.5">{EDGE_LABEL[edge.kind] || edge.kind} →</p>}
      </div>
    </li>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded border border-ek-ink/10 p-5">
      <h3 className="text-xs font-semibold uppercase tracking-[1.4px] text-ek-ink/60 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Centred({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-sm text-ek-ink/55">{children}</div>;
}
